import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai/utils';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import OpenSeaDragon from 'openseadragon';
import { useAuthUser } from 'react-auth-kit';
import { useQueryClient } from 'react-query';
import { useAssetDetailsQuery } from 'src/api/queries/assetsQueries';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { deleteAsset, getAssetLink, getAssetMediaUrl } from 'src/api/functions/assetsApi';
import { useCollectionQuery } from 'src/api/queries/collectionsQueries';
import { useCollectionMutation } from 'src/api/mutations/collectionMutations';
import useRoleManager from 'src/hooks/useRoleManager';
import { useRemoveBackgroundAvailable } from 'src/hooks/useRemoveBackgroundAvailable';
import { useImageEditAvailable } from 'src/hooks/useImageEditAvailable';
import {
  KEEP_OVERLAY_ATTR,
  SHELL_HEADER_ID,
  SHELL_SIDEBAR_ID,
  useShellOverlayTarget,
} from 'src/hooks/useShellOverlay';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { useForcedCollapsedSidebar } from 'src/components/AppSidebar/useSidebarCollapse';
import Dropdown from 'src/components/Dropdown';
import {
  X,
  FileArrowDown,
  ArrowsClockwise,
  ImageSquare,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  CaretLeft,
  CaretRight,
  PencilSimple,
  Panel,
  AssetGetLink,
  DotsThreeVertical,
  Printer,
  Stack,
  TrashSimple,
  Copy,
} from 'src/common/icons';
import { ConfirmDialog, Loader } from 'src/components';
import { FlatButton } from './components/FlatButton';
import { ModalPreview } from './ModalPreview';
import { ModalSidebar } from './ModalSidebar';
import { config } from 'src/utils/config';
import { thumbnailVersion } from 'src/utils/previews';
import { queryNames } from 'src/api/utils/queryNames';
import { CollectionResponse } from 'src/types/TCollection';
import messages from './messages';
import styles from './Modal.module.scss';
import { useAlerts } from 'src/common/AlertManager';
import at from 'src/utils/at';
import ModalEditor, { ConversionData, EDITION_MODE, OriginalData, SubtitleData } from './ModalEditor';
import AIImageEditDialog from './components/AIImageEditDialog';
import PrintExportDialog from './components/PrintExportDialog';
import AdobeConnectorDialog from './components/AdobeConnectorDialog';
import AssetLinkDialog from 'src/components/AssetLinkDialog/AssetLinkDialog';
import ChangeThumbnailDialog from 'src/components/ChangeThumbnailDialog/ChangeThumbnailDialog';
import { AgentNudgeRow } from 'src/chat/nudges';
import { CutoutDialog } from './components/CutoutDialog';
import ReplaceContentDialog from './components/ReplaceContentDialog';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { pickerModeAtom } from 'src/shared/pickerMode';
import { conversionsLostOnReplace, storedConversions } from 'src/utils/conversionLabels';
import useModal from 'src/hooks/useModal';
import AssetModalContext from './context';
import Gear from 'src/common/icons/Gear';
import useFileSpinPicker from 'src/hooks/useFileSpinPicker';
import { useFindDuplicates } from 'src/hooks/useFindDuplicates';
import { setBodyScroll } from 'src/utils/bodyScroll';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { chatMaximizedAtom } from 'src/shared/chatPreferences';
import { deletedAssetsAtom } from 'src/shared/deletedAssets';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { getSchemas } from 'src/api/functions/schemaApi';
import BoardingTour from 'src/components/BoardingTour';

type ModalParams = {
  id: string;
};

const loadLoader = (callback: () => void) => {
  const existingScript = document.getElementById('filespinLoader');
  if (!existingScript) {
    const script = document.createElement('script');
    script.src = config.filespinLoader;
    script.id = 'filespinLoader';
    document.body.appendChild(script);
    script.onload = () => {
      if (callback) callback();
    };
  }
  if (existingScript && callback) callback();
};

type EditingModeState = {
  type: EDITION_MODE;
  data: ConversionData | OriginalData | SubtitleData;
};

/**
 * Header width below which the toolbar buttons drop their text labels.
 *
 * Re-measured after the toolbar moved to the 13px tier: the labelled strip
 * needs 640px (it needed 685px at 16px), and the title/nav/close cluster now
 * flexes down to a 200px floor, so labels and a reachable close button would
 * technically coexist from ~840px up.
 *
 * The value stays at 1080 rather than following that floor down, because the
 * filename shares the same row: between 840 and 1080 the labels would only
 * survive by taking the space the name needs, and icons carry the same actions
 * with tooltips. Dropping labels earlier is the deliberate trade.
 *
 * Lowering it from 1160 is still the point at the top end: at 1080 the labels
 * survive on a 1440px screen with the AI Agent panel open (1020px of modal is
 * icon-only, 1080px is not), where the old threshold collapsed them
 * unnecessarily.
 */
const HEADER_LABELS_MIN_WIDTH = 1080;

export const Modal: React.FC<{}> = () => {
  const history = useHistory<any>();
  const findDuplicates = useFindDuplicates();
  const location = useLocation<any>();
  let { id } = useParams<ModalParams>();
  const assetDetailsQuery = useAssetDetailsQuery(id, undefined, {
    cacheTime: 0,
  });

  const { data, isLoading, refetch } = assetDetailsQuery;

  const [originalUrl, setOriginalUrl] = useState('');

  const [isZoomable, setIsZoomable] = useState(false);
  const [isZoomableActivated, setIsZoomableActivated] = useState(false);
  const [isEditModeActivated, setIsEditModeActivated] = useState(false);
  const [isCollectionMutated, setIsCollectionMutated] = useState(false);
  const [isGettingMediaUrl, setIsGettingMediaUrl] = useState(false);
  const [isGettingCdnUrl, setIsGettingCdnUrl] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  // What a Replace Content would delete; set = confirm first.
  const [replaceImpact, setReplaceImpact] = useState<{ formats: string[]; notRecreated: string[] } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAsset, setIsDeletingAsset] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [editingMode, setEditingMode] = useState<EditingModeState | null>(null);
  const [newEditingMode, setNewEditingMode] = useState<EditingModeState | null>(null);
  const [isAIEditModeActivated, setIsAIEditModeActivated] = useState(false);
  const [isCutoutModeActivated, setIsCutoutModeActivated] = useState(false);
  const [viewer, setViewer] = useState<any>(null);
  const sidebarRef = useRef<any>();
  const auth = useAuthUser();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess, showInfo, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { isUser } = useRoleManager();
  // Which formats the account's settings regenerate after a replace.
  // Users can't replace, so they don't need it.
  const { data: settings } = useSettingsQuery(undefined, { enabled: !isUser });
  const removeBackgroundAvailable = useRemoveBackgroundAvailable();
  const [deletedAssets, updateDeletedAssets] = useAtom(deletedAssetsAtom);
  const { showModal } = useModal();
  const [addonsSettings] = useAtom(addonsSettingsAtom);
  // The same check was inlined twice here (the location-state auto-open and the
  // Edit menu) while a hook for it already existed and was used by the listing
  // components. One source now.
  const aiImageEditAvailable = useImageEditAvailable();
  const [schemas, setSchemas] = useState<any>([]);
  // Content versioning: with release approval on (and this user not exempt),
  // a replacement does not change the asset — it waits on the Versions tab.
  const versioning = (settings as any)?.content_versioning;
  const pickerMode = useAtomValue(pickerModeAtom);
  const replaceIsHeld =
    !!versioning?.require_approval && !(versioning?.exempt_user_ids ?? []).includes(auth()?.userId);
  const { replaceContent, uploadContent } = useFileSpinPicker({
    events: {
      complete: () => {
        if (replaceIsHeld && pickerMode === 'replace') {
          showInfo('The new file is waiting for release. Open the Versions tab to make it live.');
        }
        queryClient.invalidateQueries([queryNames.assetVersions, id]);
        queryClient.invalidateQueries([queryNames.asset, id]);
        // The listing's thumbnail/metadata come from the search cache, so a
        // replace must refresh it too or the list stays stale.
        queryClient.invalidateQueries([queryNames.search]);
      },
    },
  });

  const fetchSchemas = async () => {
    const result = await getSchemas(accessToken);
    if (!result.data) return;
    setSchemas(result.data);
  };
  const siblings = location?.state?.siblings;

  const { formatMessage } = useIntl();

  const hasBackground = !!location?.state?.background;
  // Null outside the app shell — then we stay put and pin to the viewport.
  const overlayTarget = useShellOverlayTarget();
  const isMobile = useIsMobile();
  const chatMaximized = useAtomValue(chatMaximizedAtom);
  /*
   * Does the chat panel sit BESIDE this overlay, or on top of it? It only sits
   * beside when it's reserving side space — so not on mobile (full-width
   * drawer) and not when maximised (the shell drops its margin and the panel
   * covers the content area). When it's on top, a nudge chip has to close the
   * modal or the draft lands in a composer the user can't see.
   */
  const chatCoversModal = isMobile || chatMaximized;

  /*
   * Keep the sidebar as its icon rail while the overlay is up: the rail keeps
   * the app looking anchored (a modal with no nav at all reads as a different
   * page), but the expanded nav's labels are dead weight next to a modal you
   * can't navigate from. Releasing it on unmount restores whatever the user
   * had — see `sidebarForcedCollapsedAtom`, which deliberately never touches
   * the persisted preference.
   */
  useForcedCollapsedSidebar(hasBackground);
  const [headerNarrow, setHeaderNarrow] = useState(false);
  const headerObserverRef = useRef<ResizeObserver | null>(null);

  /*
   * Collapse the toolbar to icons when the header is too narrow for labels.
   *
   * Measured on the header, not read off the viewport: the overlay is inset
   * from the chat panel, so the width actually available is `viewport - panel`
   * and no media query can see it.
   *
   * A width threshold rather than the toolbar's own overflow, which would be
   * the more precise signal but isn't measurable: `.actions` holds an
   * auto-margin item, so its `scrollWidth` collapses onto `clientWidth`
   * whenever the content fits — there is no way to ask "how wide would the
   * labels be?" once they're hidden, and a naive re-test would oscillate.
   *
   * A callback ref rather than an effect: the header mounts only once the
   * asset query resolves (a Loader renders until then), so an effect keyed on
   * anything but the node itself would run while the ref is still null and
   * silently observe nothing.
   */
  const attachHeaderRef = useCallback((node: HTMLDivElement | null) => {
    headerObserverRef.current?.disconnect();
    headerObserverRef.current = null;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => {
      setHeaderNarrow(entry.contentRect.width < HEADER_LABELS_MIN_WIDTH);
    });
    observer.observe(node);
    headerObserverRef.current = observer;
  }, []);

  useEffect(() => () => headerObserverRef.current?.disconnect(), []);

  useEffect(() => {
    if (!hasBackground) return;

    setBodyScroll('100vh', 'hidden');

    return () => {
      setBodyScroll('', '');
    };
  }, []);
  const { data: basket, isLoading: isLoadingBasket } = useCollectionQuery();

  const basketMutation = useCollectionMutation(Number(basket?.data?.id), {
    onError: (error, newData, context: any) => {
      // Rollback mechanism
      queryClient.setQueryData([queryNames.collection, null], context.previousData);
      // TODO: Handle error, show feedback
    },
    onSettled: (data) => {
      setIsCollectionMutated(false);
    },
    onMutate: (data) => {
      setIsCollectionMutated(true);
      const previousData = queryClient.getQueryData<CollectionResponse>([queryNames.collection, null]);
      queryClient.setQueryData<CollectionResponse | undefined>(
        [queryNames.collection, null],
        (old: CollectionResponse | undefined) => {
          if (typeof old !== 'undefined') {
            old.data.assets = (old?.data?.assets ?? [])
              .filter((id) => !(data?.deletions ?? []).includes(id))
              .concat(data?.additions ?? []);
          }
          return old;
        },
      );
      return { previousData };
    },
  });

  useEffect(() => {
    fetchSchemas()
    setIsEditModeActivated(false);
  }, [id, data]);

  // Auto-open the AI Image Edit dialog when navigated here from a gallery
  // card's "AI Image Edit" action. Clear the flag so a back/refresh
  // doesn't reopen it.
  useEffect(() => {
    if (
      location?.state?.openAIEdit &&
      data &&
      data.content_type?.split('/')[0] === 'image' &&
      !data.errors?.original &&
      !isUser &&
      aiImageEditAvailable
    ) {
      handleOpenAIEdit();
      history.replace({
        pathname: location.pathname,
        state: { ...location.state, openAIEdit: false },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, location?.state?.openAIEdit]);

  useEffect(() => {
    const zoomable = data?.conversions.hasOwnProperty('deepzoom');
    const isPublicZoomable = zoomable && data?.conversions?.deepzoom?.public;
    setIsZoomable(isPublicZoomable);

    if (data && isPublicZoomable) InitOpenseadragon();
    return () => {
      viewer && viewer?.destroy();
    };
  }, [data]);

  const closeModal = () => {
    const {
      location: { state },
    } = history;

    if (!state || !state?.background) {
      // No background location: a direct /asset/:id load, a refresh, or a
      // deep link from outside the app. `goBack()` was a dead end here —
      // on a freshly opened tab there is no previous entry, so closing (or
      // finishing a delete) went nowhere, and from an external link it left
      // FileSpin entirely. Leaving an asset should land on the asset list.
      history.push('/assets');
      return;
    }

    // Restore the full background location (path + query + hash) so collection
    // deep-link filters (e.g. `?schema=669&f.status=approved`) survive closing
    // the asset modal instead of being stripped back to the bare path.
    const { pathname, search = '', hash = '' } = state.background;
    history.push({ pathname, search, hash });
  };

  const back = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    closeModal();
  };

  /*
   * Get out of the way when the user reaches for the app chrome.
   *
   * Two things go wrong otherwise. A chrome menu can paint under this
   * overlay's 100 — the old header Account menu (z-index 30) opened *behind*
   * the asset panel and a click aimed at "Logout" landed on the modal's
   * toolbar. And a search from
   * the top bar rewrites the grid the overlay is covering, so the results are
   * invisible until the asset is closed by hand. In both cases the user's action
   * has a result they cannot see.
   *
   * Links are skipped: navigating away drops the background location and
   * unmounts this component anyway, and closing first would leave a redundant
   * history entry behind. Controls whose effect is visible beside the overlay
   * opt out with KEEP_OVERLAY_ATTR.
   *
   * mousedown in the capture phase, so the overlay is already gone by the time
   * the control's own click handler runs and reveals whatever it reveals.
   */
  useEffect(() => {
    if (!hasBackground) return;

    const onChromePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || typeof target.closest !== 'function') return;
      if (target.closest(`[${KEEP_OVERLAY_ATTR}]`) || target.closest('a[href]')) return;
      if (target.closest(`#${SHELL_HEADER_ID}, #${SHELL_SIDEBAR_ID}`)) closeModal();
    };

    document.addEventListener('mousedown', onChromePointerDown, true);
    return () => document.removeEventListener('mousedown', onChromePointerDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBackground]);

  const toggleSidebar = (forceState?: boolean) => {
    setShowSidePanel(!showSidePanel);
    sidebarRef?.current?.toggleSidebar(forceState);
  };

  const InitOpenseadragon = () => {
    viewer && viewer?.destroy?.();
    setViewer(
      OpenSeaDragon({
        id: 'deepzoomContainer',
        prefixUrl: `/imagezoomableicons/`,
        tileSources: [`${config.hostUrl}/api/v1/zoomable/${id}/deepzoom.xml`],
        showNavigator: false,
        animationTime: 0.5,
        blendTime: 0.1,
        constrainDuringPan: true,
        // maxZoomPixelRatio: 2,
        minZoomLevel: 1,
        visibilityRatio: 1,
        zoomPerScroll: 2,
        showHomeControl: true,
        showNavigationControl: true,
        showZoomControl: true,
        showFullPageControl: true,
        navigatorDisplayRegionColor: '#0633DE',
      }),
    );
  };

  const handleZoomOutClick = () => {
    if (viewer?.viewport?._oldZoom <= 1) {
      setIsZoomableActivated(false);
    } else {
      viewer?.viewport?.zoomBy(0.7);
    }
  };

  const handleZoomInClick = () => {
    if (isZoomableActivated) {
      viewer?.viewport?.zoomBy(1.3);
    } else {
      setIsZoomableActivated(true);
    }
  };

  const handleNextPreviousClick = (delta: 1 | -1) => {
    const currentIdx = siblings.findIndex((a: string) => a === id);
    history.replace({
      pathname: `/asset/${siblings[currentIdx + delta]}`,
      state: {
        background: location?.state?.background,
        siblings,
      },
    });
  };

  const handleDownload = async () => {
    setIsGettingMediaUrl(true);
    const url = await getAssetMediaUrl(id, 'original', auth()!.accessId, 'download', accessToken);
    window.open(url);
    setIsGettingMediaUrl(false);
  };

  // #932: Get Link opens the link/embed dialog (temporary vs permanent
  // links, embed snippets) instead of blind-copying an unlabeled 24h URL.
  const handleGetLinkUrl = () => {
    showModal(<AssetLinkDialog assetId={id} assetName={data?.name} />);
  };
  const handleFileLoader = async () => {
    replaceContent(id);
  };

  const handleDeleteAsset = async () => {
    if (isDeletingAsset) return;
    setIsDeletingAsset(true);

    try {
      const response = await deleteAsset(id, accessToken);
      if (typeof response.status === 'number' && ![200, 202].includes(response.status)) {
        throw response;
      }

      showSuccess(formatMessage(messages.deletedAsset));
      updateDeletedAssets([...deletedAssets, id]);
      queryClient.invalidateQueries([queryNames.search]);
      closeModal();
    } catch (e: any) {
      showAlert(getErrorMessage(e.message));
    } finally {
      setIsDeletingAsset(false);
    }
  };

  const handleOverflowClick = (key: string) => {
    if (key === 'replaceContent') {
      // Confirm only when the asset has stored ("static") formats (#958) —
      // the replace deletes all of them. With just the on-demand thumbnail
      // there is nothing to lose, so go straight to the picker.
      const formats = storedConversions(data?.conversions);
      if (formats.length > 0) {
        setReplaceImpact({ formats, notRecreated: conversionsLostOnReplace(data?.conversions, settings) });
      } else {
        handleFileLoader();
      }
    }
    if (key === 'delete') setShowDeleteConfirm(true);
    if (key === 'changeThumbnail') showModal(<ChangeThumbnailDialog assetId={id} />);
  };

  const handleBasketToggle = async (state: boolean) => {
    if (typeof basket?.data?.assets === 'undefined') return;

    setIsCollectionMutated(true);
    if (state === true) {
      await basketMutation.mutateAsync({ additions: [id] });
      showSuccess(formatMessage(messages.addedToBasket));
    } else {
      await basketMutation.mutateAsync({ deletions: [id] });
      showSuccess(formatMessage(messages.removedFromBasket));
    }
    setIsCollectionMutated(false);
  };

  const handleAssetEdit = async (type: EDITION_MODE, data: OriginalData | ConversionData | SubtitleData) => {
    if (isEditModeActivated) {
      setNewEditingMode({
        type,
        data,
      });
      setShowConfirmDialog(true);
      return;
    }

    if (type === EDITION_MODE.ORIGINAL){
      const url = await getAssetMediaUrl(id, 'original', auth()!.accessId, 'display', accessToken);
      (data as OriginalData).url = url;
  }    

    setIsZoomableActivated(false);
    sidebarRef?.current?.toggleSidebar(false);
    setEditingMode({
      type,
      data,
    });
    setIsEditModeActivated(true);
  };

  const handleConfirmEditConversion = () => {
    setShowConfirmDialog(false);
    sidebarRef?.current?.toggleSidebar(false);
    setEditingMode(newEditingMode);
    setIsEditModeActivated(true);
  };

  const handleOpenAIEdit = () => {
    setIsEditModeActivated(false);
    setIsZoomableActivated(false);
    // Edit in-place inside the asset modal (same pattern as Pintura):
    // collapse the sidebar so the editor gets the full content area.
    sidebarRef?.current?.toggleSidebar(false);
    setIsAIEditModeActivated(true);
  };

  const handleCloseAIEdit = () => {
    setIsAIEditModeActivated(false);
    sidebarRef?.current?.toggleSidebar(true);
  };

  // Same shape as handleOpenAIEdit: clear the other inline panels, collapse the
  // side panel to give the stage its width, then activate.
  const handleOpenCutout = () => {
    setIsEditModeActivated(false);
    setIsZoomableActivated(false);
    setIsCutoutModeActivated(true);
    sidebarRef?.current?.toggleSidebar(false);
  };

  const handleCloseCutout = () => {
    setIsCutoutModeActivated(false);
    sidebarRef?.current?.toggleSidebar(true);
  };

  // A saved cut-out always lands somewhere new, so refresh what it touched:
  // the asset (a new conversion), the credit balance, and for a new asset the
  // gallery listing plus this asset's relationships (parent -> child).
  const handleCutoutConfirmed = (disposition: 'conversion' | 'new_asset') => {
    queryClient.invalidateQueries([queryNames.asset, id]);
    queryClient.invalidateQueries([queryNames.aiBudget]);
    if (disposition === 'new_asset') {
      queryClient.invalidateQueries([queryNames.search]);
      queryClient.invalidateQueries([queryNames.assetRelationships, id]);
    }
    handleCloseCutout();
  };

  const handleEditDropdownClick = (key: string) => {
    if (key === 'aiEditImage') {
      handleOpenAIEdit();
      return;
    }
    if (key === 'cutout') {
      handleOpenCutout();
      return;
    }
    if (key === 'adobePlugin') {
      showModal(<AdobeConnectorDialog contentType={data?.content_type} />);
      return;
    }
    // default: Pintura editor on the original image. Every non-default key MUST
    // return above — falling through here silently launches the editor.
    handleAssetEdit(EDITION_MODE.ORIGINAL, {
      name: data?.name,
      url: '', // URL is fetched inside handleAssetEdit
    });
  };

  const handleAIEditConfirmed = (disposition: 'conversion' | 'new_asset') => {
    // Refresh the asset (new conversion) and the credit balance; a brand
    // new asset also affects the gallery listing and records a parent→child
    // relationship on this (source) asset, so refresh Relationships too.
    queryClient.invalidateQueries([queryNames.asset, id]);
    queryClient.invalidateQueries([queryNames.aiBudget]);
    if (disposition === 'new_asset') {
      queryClient.invalidateQueries([queryNames.search]);
      queryClient.invalidateQueries([queryNames.assetRelationships, id]);
    }
    handleCloseAIEdit();
  };

  // Only the Pintura editor uses this now that the RBG viewer is gone.
  const handleCancelEditing = () => {
    setIsEditModeActivated(false);
  };
  const handleOnFinishEditing = useCallback(() => {
    toggleSidebar(true);
    setIsEditModeActivated(false);
  }, []);

  const getApi = useMemo(() => ({ handleOnFinishEditing }), []);

  /*
   * Placement, shared by the loading and loaded branches so they cannot drift.
   * As an overlay we portal out of the scrollable <main> into the shell's
   * content area, which pins the panel to the content pane however far the grid
   * behind it is scrolled and leaves the header, sidebar and chat panel alone.
   * Outside the shell (a direct /asset/:id load) there is no target and the page
   * renders inline.
   */
  const overlayClassName = hasBackground
    ? cn(styles.Modal, { [styles.ModalFixed]: !overlayTarget })
    : undefined;

  const portalize = (tree: React.ReactElement) =>
    hasBackground && overlayTarget ? ReactDOM.createPortal(tree, overlayTarget) : tree;

  /*
   * The loading state has to be placed exactly like the loaded one — same
   * portal, same classes. Rendering it in the route's own DOM position instead
   * put an `absolute; inset: 0` box with an opaque background in a subtree with
   * no positioned ancestor, so it resolved against the viewport and covered the
   * whole app — header, sidebar and chat panel included — until the asset query
   * came back. That read as the screen going blank rather than a panel opening.
   */
  if (isLoading)
    return portalize(
      <div onClick={back} className={overlayClassName}>
        <Loader className={styles.Loader} size="large" color="var(--color-prime-400)" />
      </div>,
    );

  const isImage = data?.content_type?.split('/')[0] === 'image';
  const isInBasket = !!basket?.data?.assets.includes(id);
  // The Adobe plugin covers Photoshop, InDesign and Premiere Pro, so it is
  // offered for video as well as images. Hidden entirely until the deployment
  // has a marketplace URL configured — see config.adobePluginUrl for why that
  // doubles as the feature gate.
  const adobePluginAvailable =
    Boolean(config.adobePluginUrl) && (isImage || data?.content_type?.startsWith('video'));

  const overlay = (
    <AssetModalContext.Provider value={getApi}>
      <div onClick={back} className={overlayClassName}>
        <div className={hasBackground ? styles.wrapper : styles.container} onClick={(e) => e.stopPropagation()}>
          <div
            className={cn(styles.header, { [styles.headerNarrow]: headerNarrow })}
            ref={attachHeaderRef}
          >
            <div className={styles.actions}>
              {!isEditModeActivated && !isAIEditModeActivated && !isCutoutModeActivated && (
                <>
                  {/* Leave: back to wherever the asset was opened from — the
                      list, a collection (filters and all, see closeModal), the
                      basket — falling back to the asset list on a direct load.
                      Always offered: the X beside the title only appears when
                      the modal has a background location, so a deep-linked
                      asset had no way out but the browser's own Back. */}
                  <div className={styles.group}>
                    <FlatButton lightIcon onClick={back} icon={<CaretLeft />} datatest="back-to-previous">
                      <FormattedMessage {...messages.back} />
                    </FlatButton>
                  </div>

                  {/* Consumption: actions that read the asset, never change it */}
                  <div className={styles.group}>
                    <FlatButton
                      disabled={isGettingMediaUrl}
                      lightIcon
                      onClick={handleDownload}
                      icon={<FileArrowDown />}
                    >
                      <FormattedMessage {...messages.downloadContent} />
                    </FlatButton>
                    <FlatButton
                      lightIcon
                      onClick={handleGetLinkUrl}
                      icon={<AssetGetLink loading={false} />}
                    >
                      <FormattedMessage {...messages.getLinkContent} />
                    </FlatButton>
                    {/* Reads only, so every role gets it. Without a checksum
                        (still processing) there is nothing to match on. */}
                    {data?.checksum && (
                      <FlatButton
                        lightIcon
                        onClick={() => findDuplicates({ id, name: data?.name })}
                        icon={<Copy />}
                        datatest="find-duplicates-button"
                      >
                        <FormattedMessage {...messages.findDuplicates} />
                      </FlatButton>
                    )}
                    {!isUser &&
                      isImage &&
                      !data.errors?.original &&
                      addonsSettings?.PRINT_EXPORT?.added &&
                      addonsSettings?.PRINT_EXPORT?.enabled && (
                        <FlatButton
                          lightIcon
                          onClick={() => showModal(<PrintExportDialog assetId={id} assetName={data?.name || ''} />)}
                          icon={<Printer />}
                          datatest="print-export-button"
                        >
                          <FormattedMessage {...messages.exportForPrint} />
                        </FlatButton>
                      )}
                  </div>

                  {/* Basket membership toggle */}
                  <div className={styles.group}>
                    <FlatButton
                      className={isInBasket ? styles.basketActive : undefined}
                      disabled={isCollectionMutated || data.errors?.original}
                      lightIcon
                      onClick={() => handleBasketToggle(!isInBasket)}
                      icon={<Stack />}
                      datatest="basket-toggle"
                    >
                      <FormattedMessage {...(isInBasket ? messages.inBasket : messages.add)} />
                    </FlatButton>
                  </div>

                  {/* Mutation: editors behind a single Edit menu. Every in-app
                      editor is image-only, but the Adobe plugin covers Premiere
                      Pro as well — so the menu itself is no longer gated on
                      `isImage`, and each option carries its own gate. A video
                      asset gets an Edit menu holding only the Adobe item. */}
                  {!isUser && !data.errors?.original && (isImage || adobePluginAvailable) && (
                    <div>
                      <Dropdown
                        /* Borderless trigger, so it belongs to the same family
                           as the Download / Get Link / Export buttons beside
                           it — the caret is what signals it opens a menu. */
                        variant="filter"
                        dropdownAnchor="left"
                        dropdownDirection="down"
                        onClick={handleEditDropdownClick}
                        options={[
                          ...(isImage
                            ? [{ key: 'editImage', value: formatMessage(messages.editImage) }]
                            : []),
                          ...(isImage &&
                          addonsSettings?.AI_IMAGE_EDIT?.added &&
                          addonsSettings?.AI_IMAGE_EDIT?.enabled
                            ? [{ key: 'aiEditImage', value: formatMessage(messages.aiEditImage) }]
                            : []),
                          ...(isImage && (removeBackgroundAvailable || aiImageEditAvailable)
                            ? [{ key: 'cutout', value: formatMessage(messages.cutoutMenu) }]
                            : []),
                          ...(adobePluginAvailable
                            ? [{ key: 'adobePlugin', value: formatMessage(messages.adobePlugin) }]
                            : []),
                        ]}
                      >
                        <span className={styles.editTrigger}>
                          <PencilSimple />
                          <FormattedMessage {...messages.editMenu} />
                        </span>
                      </Dropdown>
                    </div>
                  )}

                  {/* Overflow: destructive / rare actions behind a kebab */}
                  {!isUser && (
                    <div className={styles.overflowMenu}>
                      <Dropdown
                        variant="menu"
                        dropdownAnchor="left"
                        icon={<DotsThreeVertical />}
                        onClick={handleOverflowClick}
                        options={[
                          {
                            key: 'changeThumbnail',
                            value: formatMessage(messages.changeThumbnail),
                            icon: <ImageSquare />,
                          },
                          {
                            key: 'replaceContent',
                            value: formatMessage(messages.replaceContent),
                            icon: <ArrowsClockwise />,
                          },
                          {
                            key: 'delete',
                            value: formatMessage(messages.deleteAsset),
                            icon: <TrashSimple />,
                            optionDisabled: isDeletingAsset,
                          },
                        ]}
                      />
                    </div>
                  )}
                </>
              )}
              {isEditModeActivated && (
                <FlatButton lightIcon onClick={handleCancelEditing} icon={<PencilSimple />} datatest="close-edit">
                  <FormattedMessage {...messages.cancelEditImage} />
                </FlatButton>
              )}
              {isAIEditModeActivated && (
                <FlatButton lightIcon onClick={handleCloseAIEdit} icon={<CaretLeft />} datatest="close-ai-edit">
                  <FormattedMessage {...messages.back} />
                </FlatButton>
              )}
              {isCutoutModeActivated && (
                <FlatButton lightIcon onClick={handleCloseCutout} icon={<CaretLeft />} datatest="close-cutout">
                  <FormattedMessage {...messages.back} />
                </FlatButton>
              )}
            </div>
            <div className={styles.controls}>
              <div className={styles.title}>
                {data.errors?.original && <div className={styles.errortext}>ERROR</div>}
                {data.name}
              </div>

              <div className={styles.push}>
                {siblings && !isAIEditModeActivated && !isCutoutModeActivated && (
                  <>
                    <FlatButton
                      disabled={id === siblings[0]}
                      onClick={() => handleNextPreviousClick(-1)}
                      icon={<CaretLeft />}
                    />
                    <FlatButton
                      disabled={id === at(siblings, -1)}
                      onClick={() => handleNextPreviousClick(1)}
                      icon={<CaretRight />}
                    />
                  </>
                )}
              </div>

              <div>
                <FlatButton onClick={() => toggleSidebar()} icon={<Panel />} />
              </div>
              {hasBackground && (
                <div>
                  <FlatButton onClick={back} icon={<X />} datatest="close-asset-modal" />
                </div>
              )}
            </div>
          </div>
          <>
            {/* Normally the overlay is inset from the chat panel, so a chip
                lands the draft in a composer the user can already see and the
                asset stays on screen beside it — the whole point of the
                full-bleed layout. Only when the panel would cover the overlay
                do we fall back to closing the modal. */}
            <AgentNudgeRow
              surface="asset-detail"
              claimSelection
              assetIds={[id]}
              contentTypes={[data.content_type ?? '']}
              onActivate={hasBackground && chatCoversModal ? closeModal : undefined}
            />
            <div className={styles.content}>
              <div className={styles.preview}>
                {data.status === 'NOT_READY' && (
                  <div className={styles.processingAsset}>
                    <Gear /> <span>Processing</span>
                  </div>
                )}
                <div
                  id="deepzoomContainer"
                  style={{ width: '100%', height: '100%', display: isZoomableActivated ? 'block' : 'none' }}
                ></div>
                {isZoomable &&
                  isImage &&
                  !data.errors?.original &&
                  !isEditModeActivated &&
                  !isAIEditModeActivated &&
                  !isCutoutModeActivated && (
                    <div className={styles.zoomControls}>
                      <FlatButton onClick={handleZoomOutClick} icon={<MagnifyingGlassMinus />} />
                      <FlatButton onClick={handleZoomInClick} icon={<MagnifyingGlassPlus />} />
                    </div>
                  )}
                {!isEditModeActivated &&
                  !isZoomableActivated &&
                  !isAIEditModeActivated &&
                  !isCutoutModeActivated && (
                  <ModalPreview
                    assetType={data.content_type}
                    assetId={id}
                    conversions={data.conversions}
                    dimensions={[data.metadata?.width, data.metadata?.height]}
                    thumbnailVersion={thumbnailVersion(data)}
                  />
                )}
                {isEditModeActivated && editingMode && (
                  <ModalEditor assetId={id} type={editingMode.type} data={editingMode.data} metadata={data} />
                )}
                {isAIEditModeActivated && (
                  <AIImageEditDialog
                    assetId={id}
                    assetName={data.name}
                    assetWidth={data.metadata?.width}
                    assetHeight={data.metadata?.height}
                    onClose={handleCloseAIEdit}
                    onConfirmed={handleAIEditConfirmed}
                  />
                )}
                {isCutoutModeActivated && (
                  <CutoutDialog
                    assetId={id}
                    assetName={data.name}
                    assetData={data.data}
                    onConfirmed={handleCutoutConfirmed}
                  />
                )}
              </div>
              <ModalSidebar asset={data} schemas={schemas} ref={sidebarRef} onEditConversion={handleAssetEdit} />
            </div>
          </>
        </div>
      </div>
      {showConfirmDialog && (
        <ConfirmDialog
          title={formatMessage(messages.editConfirmDialogTitle)}
          message={formatMessage(messages.editConfirmDialogMessage)}
          onConfirm={handleConfirmEditConversion}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}
      {replaceImpact && (
        <ReplaceContentDialog
          formats={replaceImpact.formats}
          notRecreated={replaceImpact.notRecreated}
          onReplace={() => {
            setReplaceImpact(null);
            handleFileLoader();
          }}
          onUploadAsNew={() => {
            setReplaceImpact(null);
            uploadContent();
          }}
          onCancel={() => setReplaceImpact(null)}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmDialog
          title={formatMessage(messages.deleteConfirmTitle)}
          message={formatMessage(messages.deleteConfirmMessage)}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            handleDeleteAsset();
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </AssetModalContext.Provider>
  );

  return (
    <>
      <BoardingTour
        title="Asset Page"
        description="This page shows the details of the single asset"
        path="/asset"
        uniqueId="ASSET_PAGE"
      />
      {portalize(overlay)}
    </>
  );
};
